import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Checkbox } from '@quebon/ui/components/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { Field, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { handleApiError } from '@/api/client'
import { briefingSessionApi, type BriefingAttendanceStatus, type BriefingSessionAdmin } from '@/api/lcMarketing'
import { ErrorBlock, LabeledSelect } from '../../crm/crmComponents'
import { SESSION_MODE_LABELS } from './inquiryLabels'

const ATTENDANCE_ITEMS = [
  { value: 'ATTENDED', label: '참석' },
  { value: 'NO_SHOW', label: '불참' },
  { value: 'UNKNOWN', label: '미기록' },
]

export function BriefingAttendanceDialog({ session, onClose }: { session: BriefingSessionAdmin; onClose: () => void }) {
  const client = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [draft, setDraft] = useState<Record<number, BriefingAttendanceStatus>>({})
  const roster = useQuery({
    queryKey: ['briefing-attendance', session.id, search],
    queryFn: () => briefingSessionApi.attendance(session.id, search),
  })
  const refresh = () => {
    for (const queryKey of [
      ['briefing-attendance', session.id], ['briefing-applications'], ['briefing-sessions-admin'],
      ['lc-crm-inbox-customers'], ['lc-crm-inbox-customer-detail'], ['lc-crm-intake-detail'],
      ['lc-crm-dashboard'], ['lc-crm-funnel'],
    ]) client.invalidateQueries({ queryKey })
  }
  const save = useMutation({
    mutationFn: () => briefingSessionApi.saveAttendance(session.id, Object.entries(draft).map(([id, attendanceStatus]) => ({ applicationId: Number(id), attendanceStatus }))),
    onSuccess: () => {
      setDraft({})
      setSelected(new Set())
      refresh()
      toast.success('출석부를 저장했습니다. CRM 접수함에도 반영됩니다.')
    },
    onError: (error) => {
      refresh()
      toast.error(handleApiError(error) || '출석부 저장에 실패했습니다. 신청 상태를 확인해주세요.')
    },
  })
  const rows = roster.data?.applications ?? []
  const activeRows = rows.filter((row) => row.participationStatus === 'active')
  const allSelected = activeRows.length > 0 && activeRows.every((row) => selected.has(row.id))
  const draftCount = Object.keys(draft).length
  const setAttendance = (id: number, value: string, previous: BriefingAttendanceStatus) => {
    if (value !== 'ATTENDED' && value !== 'NO_SHOW' && value !== 'UNKNOWN') return
    setDraft((current) => {
      const next = { ...current }
      if (value === previous) delete next[id]
      else next[id] = value
      return next
    })
  }
  const bulk = (status: BriefingAttendanceStatus) => {
    setDraft((current) => {
      const next = { ...current }
      for (const row of activeRows) {
        if (!selected.has(row.id)) continue
        if (row.attendanceStatus === status) delete next[row.id]
        else next[row.id] = status
      }
      return next
    })
  }
  const close = () => {
    if (save.isPending) return
    if (draftCount > 0 && !window.confirm('저장하지 않은 출석 변경을 닫을까요?')) return
    onClose()
  }
  const date = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(session.startsAt))

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close() }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>사업설명회 출석부</DialogTitle>
          <DialogDescription>{date} · {SESSION_MODE_LABELS[session.mode]} · 참석 여부를 변경한 뒤 일괄 저장합니다.</DialogDescription>
        </DialogHeader>
        {roster.data && (
          <div aria-label="회차 참석 집계" className="flex flex-wrap gap-2">
            <Badge variant="outline">신청 {roster.data.summary.total}명</Badge>
            <Badge>참석 {roster.data.summary.attended}명</Badge>
            <Badge variant="secondary">불참 {roster.data.summary.noShow}명</Badge>
            <Badge variant="outline">미기록 {roster.data.summary.unknown}명</Badge>
            <Badge variant="outline">신청 취소 {roster.data.summary.cancelled}명</Badge>
          </div>
        )}
        <div className="flex items-end gap-2">
          <Field className="flex-1">
            <FieldLabel htmlFor="attendance-search">이름·전화번호 검색</FieldLabel>
            <Input id="attendance-search" value={searchInput} placeholder="이름 또는 전화번호" onChange={(event) => setSearchInput(event.target.value)} onKeyDown={(event) => {
              if (event.key === 'Enter') { setSearch(searchInput.trim()); setSelected(new Set()) }
            }} />
          </Field>
          <Button variant="outline" disabled={save.isPending} onClick={() => { setSearch(searchInput.trim()); setSelected(new Set()) }}>검색</Button>
        </div>
        <p className="text-xs text-muted-foreground">집계는 검색과 관계없이 회차 전체의 활성 신청 기준입니다.</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">선택 {selected.size}명</span>
          <Button variant="outline" size="sm" disabled={selected.size === 0 || save.isPending} onClick={() => bulk('ATTENDED')}>선택 참석</Button>
          <Button variant="outline" size="sm" disabled={selected.size === 0 || save.isPending} onClick={() => bulk('NO_SHOW')}>선택 불참</Button>
          <Button variant="ghost" size="sm" disabled={selected.size === 0 || save.isPending} onClick={() => bulk('UNKNOWN')}>선택 미기록</Button>
        </div>
        {roster.isLoading && <div className="flex justify-center gap-2 py-8 text-muted-foreground"><Spinner />출석부를 불러오는 중입니다.</div>}
        {roster.error && <ErrorBlock message="출석부를 불러오지 못했습니다." />}
        {roster.data && <Table>
          <TableHeader>
            <TableRow>
              <TableHead><Checkbox aria-label="출석부 전체 선택" checked={allSelected} disabled={activeRows.length === 0 || save.isPending} onCheckedChange={(checked) => setSelected(checked ? new Set(activeRows.map((row) => row.id)) : new Set())} /></TableHead>
              <TableHead>이름</TableHead><TableHead>전화번호</TableHead><TableHead>지역</TableHead><TableHead>신청 상태</TableHead><TableHead>참석 여부</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">신청자가 없습니다.</TableCell></TableRow>}
            {rows.map((row) => <TableRow key={row.id}>
              <TableCell><Checkbox aria-label={`${row.name} 선택`} checked={selected.has(row.id)} disabled={row.participationStatus === 'cancelled' || save.isPending} onCheckedChange={(checked) => setSelected((current) => {
                const next = new Set(current)
                if (checked) next.add(row.id)
                else next.delete(row.id)
                return next
              })} /></TableCell>
              <TableCell>{row.name}{draft[row.id] && <Badge variant="outline" className="ml-2">변경</Badge>}</TableCell>
              <TableCell className="tabular-nums">{row.phone}</TableCell>
              <TableCell>{[row.sido, row.sigungu].filter(Boolean).join(' ') || '-'}</TableCell>
              <TableCell>{row.participationStatus === 'active' ? '활성' : '취소'}</TableCell>
              <TableCell>{row.participationStatus === 'cancelled' || save.isPending
                ? ATTENDANCE_ITEMS.find((item) => item.value === row.attendanceStatus)?.label
                : <LabeledSelect ariaLabel={`${row.name} 참석 여부`} size="sm" value={draft[row.id] ?? row.attendanceStatus} items={ATTENDANCE_ITEMS} onChange={(next) => setAttendance(row.id, next, row.attendanceStatus)} />}</TableCell>
            </TableRow>)}
          </TableBody>
        </Table>}
        <DialogFooter>
          <Button variant="outline" disabled={save.isPending} onClick={close}>닫기</Button>
          <Button disabled={draftCount === 0 || save.isPending} onClick={() => save.mutate()}>{save.isPending && <Spinner data-icon="inline-start" />}{draftCount}명 출석 저장</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
